<template>
  <div class="container v-content-container" :key="componentKey">
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-user fa-fw fa-users"></i>
          {{ $label("organization.organizations", "Organizations") }}
          <router-link
            :to="{ name: 'NewOrganization' }"
            class="btn btn-v-lightgreen-white pull-right"
            v-tooltip="{
              content: $label(
                'organization.new_organization',
                'New organization'
              ),
              classes: 'custom-tooltip'
            }"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </router-link>
        </h1>
      </div>
    </div>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <alert-box
      v-show="failedRequest"
      type="warning"
      :closeable="false"
      :show-icon="false"
    >
      {{
        $label(
          "organization.organizations_failed_request",
          "Request failed while loading organizations."
        )
      }}
    </alert-box>

    <Table
      v-show="!failedRequest"
      id="organizations-table"
      :loading="loading"
      :columns="[
        {
          title: 'Name',
          field: 'name',
          id: row => `organization-name-${row.id}`
        },
        {
          title: 'Type',
          field: 'type',
          id: row => `organization-type-${row.id}`
        },
        {
          title: 'Email',
          field: 'email',
          id: row => `organization-email-${row.id}`
        },
        {
          title: 'Website',
          field: 'website',
          id: row => `organization-website-${row.id}`
        }
      ]"
      :actions="[
        {
          type: 'detail',
          onClick: row => {
            $router.push({
              name: 'OrganizationDetails',
              params: { organization_id: row.id }
            });
          }
        },
        {
          icon: 'envelope-o',
          tooltip: 'Email to this organization',
          onClick: () => {
            $router.push('#');
          }
        },
        {
          icon: 'mobile',
          bgColor: 'primary',
          tooltip: 'SMS to this organization',
          onClick: () => {
            $router.push('#');
          }
        },
        {
          type: 'delete',
          id: row => `delete-organization-button-${row.id}`,
          onClick: row => deleteOrganization(row.id)
        }
      ]"
      :data="organizations"
      :meta="metaData"
      @fetchQuery="
        params => {
          fetchParams = params;
          getOrganizations({
            //
          });
        }
      "
    >
      <div
        id="action-dropdown"
        slot="toolbar"
        class="btn-group"
        style="margin-left: 5px;"
      >
        <button
          type="button"
          class="btn btn-primary dropdown-toggle"
          data-toggle="dropdown"
        >
          <i class="fa fa-refresh"></i> &nbsp;
          {{ $label("common.actions", "Actions") }} &nbsp;<span
            class="caret"
          ></span>
        </button>
        <ul class="dropdown-menu" role="menu">
          <li>
            <router-link :to="{ name: 'NewOrganization' }"
              ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
              {{
                $label("organization.new_organization", "New organization")
              }}</router-link
            >
          </li>
        </ul>
      </div>
    </Table>

    <div class="row mb-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>
  </div>
</template>

<script>
import Api from "@/api";
import swal from "sweetalert";

import Table from "@/components/Common/Table";
import AlertBox from "@/components/Common/Alerts/AlertBox";

export default {
  name: "Organizations",

  components: {
    Table,
    AlertBox
  },

  data() {
    return {
      organizations: [],
      loading: false,
      updating: false,
      failedRequest: false,
      metaData: null,
      fetchParams: {
        page: 1,
        per_page: null,
        to: 1,
        search: ""
      },
      selectedOrganizationId: null,
      componentKey: 0
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (
        vm.$route.query.organization_deleted === true ||
        vm.$route.query.organization_deleted === "true"
      ) {
        swal("Success!", "The organization was deleted.", "success").then(
          () => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          }
        );
      }
      vm.getOrganizations(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.organization_deleted === true) {
      swal("Success!", "The organization was deleted.", "success").then(() => {
        window.history.pushState({}, document.title, window.location.pathname);
      });
    }
    this.forceRerender();
    if (
      this.fetchParams.to % this.fetchParams.per_page === 0 &&
      this.fetchParams.page !== 1
    ) {
      this.fetchParams.page -= 1;
    }
    this.getOrganizations(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    getOrganizations(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.organizations = [];
      const endpoint = this.$partnerEndpoint("organizations");

      Api()
        .get(endpoint, {
          params: {
            page: this.fetchParams.page,
            search: this.fetchParams.search
          }
        })
        .then(response => {
          this.organizations = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deleteOrganization(organizationId) {
      this.selectedOrganizationId = organizationId;
      if (
        this.loading ||
        this.updating ||
        this.selectedOrganizationId == null
      ) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The organization will be deleted.",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "organizations/" + this.selectedOrganizationId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "OrganizationsOverview",
                  query: { organization_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal(
                      "Success!",
                      "The organization was deleted.",
                      "success"
                    );
                    this.getOrganizations(() => {
                      //
                    });
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete organization.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    forceRerender() {
      this.componentKey += 1;
    }
  }
};
</script>

<style scoped>
.mt-0 {
  margin-top: 0rem;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0rem;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}

.min-width-auto {
  width: 50px;
}
</style>
